import type { OrganizationRankingResponse } from '@on3/api';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import styles from './TeamRow.module.scss';

interface ITeamRowProps extends OrganizationRankingResponse {
  // We need this added to the response
  conferenceRank: number;
}

export const TeamRow = ({
  overallConsensusRank,
  overallRank,
  fiveStars,
  consensusFiveStars,
  fourStars,
  consensusFourStars,
  threeStars,
  consensusThreeStars,
  commits,
  organization,
  appliedTotalRating,
  appliedTotalConsensusRating,
  dispayOn3Score,
  dispayConsensusScore,
  conferenceRank,
  averageNilValue,
}: ITeamRowProps) => {
  const router = useRouter();
  const { rankingType, year, sport, conference } = router.query;
  const isIndustry = useMemo(() => {
    return rankingType === 'industry-team';
  }, [rankingType]);
  const conferenceRankValue = conferenceRank !== null ? conferenceRank : '-';
  const industryRankValue =
    overallConsensusRank !== null ? overallConsensusRank : '-';
  const on3OverallRank = overallRank !== null ? overallRank : '-';

  return (
    <div className={styles.teamContainer}>
      <span>
        <Text variant="h6">
          {conference
            ? conferenceRankValue
            : isIndustry
              ? industryRankValue
              : on3OverallRank}
        </Text>
      </span>
      <span className={styles.logoLink}>
        {organization?.slug && (
          <>
            <TeamAvatar
              className={styles.logo}
              height={30}
              slug={organization?.name}
              src={organization.assetUrl}
              width={30}
            />

            <TextLink
              className={styles.teamName}
              href={`/college/${organization.slug}/${sport}/${year}/industry-comparison-commits/`}
              variant="h5"
            >
              {organization.name}
            </TextLink>
          </>
        )}
      </span>
      <div className={styles.starsContainer}>
        <div className={styles.fiveStars}>
          <Text
            className={styles.badgeTitle}
            component="span"
            variant="subtitle1"
          >
            5-Stars
          </Text>
          <Text className={styles.badgeNumber}>
            {isIndustry ? consensusFiveStars : fiveStars}
          </Text>
        </div>
        <div className={styles.fourStars}>
          <Text
            className={styles.badgeTitle}
            component="span"
            variant="subtitle1"
          >
            4-Stars
          </Text>
          <Text className={styles.badgeNumber}>
            {isIndustry ? consensusFourStars : fourStars}
          </Text>
        </div>
        <div className={styles.threeStars}>
          <Text
            className={styles.badgeTitleColor}
            component="span"
            variant="subtitle1"
          >
            3-Stars
          </Text>
          <Text className={styles.badgeNumberColor}>
            {isIndustry ? consensusThreeStars : threeStars}
          </Text>
        </div>
        <div className={styles.totalCommits}>
          <Text
            className={styles.badgeTitleColor}
            component="span"
            variant="subtitle1"
          >
            Total
          </Text>
          <Text className={styles.badgeNumberColor}>{commits}</Text>
        </div>
      </div>

      <span className={styles.averageRating}>
        <Text
          aria-label="Average"
          className={styles.mobileAvgLabel}
          variant="subtitle1"
        >
          Avg.
        </Text>
        <Text className={styles.txtRating} variant="subtitle1">
          {(isIndustry
            ? appliedTotalConsensusRating
            : appliedTotalRating
          )?.toFixed(2) || '-'}
        </Text>
      </span>
      <span className={styles.averageNil}>
        <Text
          aria-label="Average NIL"
          className={styles.mobileAvgLabel}
          variant="subtitle1"
        >
          Avg. NIL
        </Text>
        <Text className={styles.txtRating} variant="subtitle1">
          {averageNilValue ? formatCurrency(averageNilValue) : '-'}
        </Text>
      </span>
      <span className={styles.score}>
        <Text className={styles.txtScore}>
          {(isIndustry ? dispayConsensusScore : dispayOn3Score)?.toFixed(3) ||
            '-'}
        </Text>
      </span>
    </div>
  );
};
